import React from 'react';
import type { PromotionRelegationMove, PromotionPreview } from '@sulis-liiga/common';

interface PromotionRelegationZoneProps {
  preview: PromotionPreview;
}

/**
 * PromotionRelegationZone component
 *
 * Displays a visual preview of which teams will be promoted or relegated,
 * showing the movement arrows and division changes.
 */
const PromotionRelegationZone: React.FC<PromotionRelegationZoneProps> = ({ preview }) => {
  const MoveCard: React.FC<{ move: PromotionRelegationMove }> = ({ move }) => {
    const isPromotion = move.type === 'promotion';
    return (
      <div
        className={`flex items-center justify-between p-3 rounded-lg border ${
          isPromotion ? 'border-green-200 bg-green-50' : 'border-danger-200 bg-danger-50'
        }`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{move.teamName}</p>
          <p className="text-xs text-gray-500 truncate">{move.fromDivisionName}</p>
        </div>
        <div className="mx-3 flex flex-col items-center">
          {isPromotion ? (
            <svg
              className="h-5 w-5 text-green-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-label="Promotion"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
          ) : (
            <svg
              className="h-5 w-5 text-danger-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-label="Relegation"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0 text-right">
          <p className="text-xs text-gray-500 truncate">{move.toDivisionName}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Promotions section */}
      {preview.promotions.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
            Promotions ({preview.promotions.length})
          </h3>
          <div className="space-y-2">
            {preview.promotions.map(move => (
              <MoveCard key={move.teamId} move={move} />
            ))}
          </div>
        </div>
      )}

      {/* Relegations section */}
      {preview.relegations.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-danger-800 mb-3 flex items-center gap-2">
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
            Relegations ({preview.relegations.length})
          </h3>
          <div className="space-y-2">
            {preview.relegations.map(move => (
              <MoveCard key={move.teamId} move={move} />
            ))}
          </div>
        </div>
      )}

      {/* Teams without next division */}
      {preview.teamsWithoutNextDivision.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-yellow-800 mb-3 flex items-center gap-2">
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            No target division ({preview.teamsWithoutNextDivision.length})
          </h3>
          <div className="space-y-2">
            {preview.teamsWithoutNextDivision.map(team => (
              <div
                key={team.teamId}
                className="flex items-start p-3 rounded-lg border border-yellow-200 bg-yellow-50"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">{team.teamName}</p>
                  <p className="text-xs text-yellow-700">{team.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {preview.promotions.length === 0 &&
        preview.relegations.length === 0 &&
        preview.teamsWithoutNextDivision.length === 0 && (
          <p className="text-center text-gray-500 text-sm">No movements to process.</p>
        )}
    </div>
  );
};

export default PromotionRelegationZone;
